import React, { useEffect, useState } from 'react';
import { Info, MessageCircle, Mail } from 'lucide-react';
import { catalogGroups } from '../catalog';
import { companyInfo } from '../mock';
import Navbar from '../components/Navbar';
import Reveal from '../components/Reveal';
import Lightbox from '../components/Lightbox';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';

const CatalogPage = () => {
  const [open, setOpen] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Catalogue | Cherry Stone';
  }, []);

  const whatsappHref = `https://wa.me/${companyInfo.whatsapp}?text=${encodeURIComponent(
    "Hi Cherry Stone, I'm browsing your catalogue and would like to enquire about"
  )}`;
  const mailHref = `mailto:${companyInfo.email}?subject=${encodeURIComponent(
    'Cherry Stone \u2014 Catalogue Enquiry'
  )}`;

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#1a1a1a]">
      <Navbar />

      {/* Intro */}
      <section className="relative pt-32 md:pt-40 pb-10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <span className="section-num text-sm">Currently in stock</span>
            <h1 className="mt-3 font-serif text-4xl md:text-6xl leading-[1.05] max-w-4xl">
              The Cherry Stone <span className="italic">Catalogue</span>.
            </h1>
            <p className="mt-6 max-w-2xl text-[#4a463f] text-[15.5px] md:text-[17px] leading-[1.85]">
              A live look at the slabs waiting on our factory floor at Pioneer Lot. Every piece
              below is available for viewing and quotation today.
            </p>
          </Reveal>

          {/* Sourcing note */}
          <Reveal delay={150}>
            <div className="mt-10 flex flex-col md:flex-row gap-5 items-start p-6 md:p-7 rounded-sm bg-white border border-[#E5DED0]">
              <div className="w-11 h-11 rounded-full bg-[#F1EBE1] flex items-center justify-center text-[#7B1E24] flex-shrink-0">
                <Info size={18} strokeWidth={1.6} />
              </div>
              <div className="flex-1">
                <div className="text-[11px] tracking-[0.28em] uppercase text-[#B8935A]">Not in stock?</div>
                <p className="mt-1 text-[#1a1a1a] text-[15px] md:text-[16px] leading-relaxed">
                  If your preferred stone is not found in our catalogue, we can source it for you.
                  We will check availability directly with our quarry partners and confirm
                  the estimated timeline for your project.
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#7B1E24] text-white text-[12px] tracking-[0.22em] uppercase font-medium hover:bg-[#5C1319] transition-colors"
                  >
                    <MessageCircle size={14} /> WhatsApp us
                  </a>
                  <a
                    href={mailHref}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-[#1a1a1a]/30 text-[#1a1a1a] text-[12px] tracking-[0.22em] uppercase font-medium hover:bg-[#1a1a1a] hover:text-white transition-colors"
                  >
                    <Mail size={14} /> Email us
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Stone type sections */}
      {catalogGroups.map((group, gIdx) => (
        <section key={group.type} className="py-12 md:py-16">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <Reveal>
              <div className="flex items-end justify-between gap-6 mb-8 md:mb-12 border-b border-[#E5DED0] pb-6">
                <div>
                  <span className="section-num text-xs">0{gIdx + 1}</span>
                  <h2 className="mt-1 font-serif text-3xl md:text-[42px] leading-[1.05] text-[#1a1a1a]">
                    {group.type}
                  </h2>
                  <p className="mt-1 text-[13px] tracking-wide text-[#B8935A] italic">
                    {group.tagline}
                  </p>
                </div>
                <div className="text-[11px] tracking-[0.28em] uppercase text-[#7a7268] whitespace-nowrap">
                  {group.stones.length} in stock
                </div>
              </div>
            </Reveal>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
              {group.stones.map((stone, i) => (
                <Reveal key={stone.name} delay={(i % 4) * 60}>
                  <button
                    type="button"
                    onClick={() =>
                      setOpen({
                        project: { title: stone.name, category: `${group.type} \u2022 In Stock`, image: stone.image },
                        startIdx: 0,
                      })
                    }
                    className="group w-full text-left card-lift bg-white rounded-sm overflow-hidden border border-[#E5DED0]"
                    aria-label={`Zoom ${stone.name}`}
                  >
                    <div
                      className="aspect-[4/3] transition-transform duration-[900ms] group-hover:scale-[1.03]"
                      style={{
                        backgroundImage: `url(${stone.image})`,
                        backgroundSize: stone.bgSize || 'cover',
                        backgroundPosition: stone.bgPosition || 'center',
                        backgroundRepeat: 'no-repeat',
                        backgroundColor: stone.bgColor || '#F1EBE1',
                      }}
                      role="img"
                      aria-label={stone.name}
                    />
                    <div className="p-4 md:p-5 flex items-center justify-between gap-3">
                      <div>
                        <div className="font-serif text-lg md:text-xl text-[#1a1a1a] leading-tight">
                          {stone.name}
                        </div>
                        <div className="mt-1 text-[10px] tracking-[0.28em] uppercase text-[#B8935A]">
                          {group.type}
                        </div>
                      </div>
                      <span className="text-[11px] tracking-[0.22em] uppercase text-[#7B1E24] opacity-0 group-hover:opacity-100 transition-opacity">
                        Zoom
                      </span>
                    </div>
                  </button>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Bottom CTA */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="rounded-sm bg-[#0f0d0b] text-white p-8 md:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="max-w-xl">
              <div className="text-[11px] tracking-[0.28em] uppercase text-[#B8935A]">Ready to view a slab?</div>
              <h3 className="mt-2 font-serif text-3xl md:text-[38px] leading-[1.1]">
                Visit our factory or request a sample.
              </h3>
              <p className="mt-4 text-white/75 text-[15px] leading-relaxed">
                Slabs are best seen in person. Drop by Pioneer Lot or reach out and we&rsquo;ll
                send high-resolution photos and dimensions.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <a
                href={whatsappHref}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#25D366] text-white text-[12px] tracking-[0.22em] uppercase font-medium hover:brightness-110 transition"
              >
                <MessageCircle size={14} /> WhatsApp
              </a>
              <a
                href={mailHref}
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-[#1a1a1a] text-[12px] tracking-[0.22em] uppercase font-medium hover:bg-[#F1EBE1] transition"
              >
                <Mail size={14} /> Email
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />

      {open && (
        <Lightbox project={open.project} startIdx={open.startIdx} onClose={() => setOpen(null)} />
      )}
    </div>
  );
};

export default CatalogPage;
